import { Show } from "@refinedev/antd";
import { useCan, useNotification, useOne } from "@refinedev/core";
import { Button, Card, Col, Descriptions, Empty, Row, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { api } from "../api";

import {
  CANCELLATION_REASONS,
  CANCELLATION_REQUEST_STATUS,
  DATE_TIME_FORMAT,
  HOUR_BUCKETS,
  HOUR_REASONS,
  LESSON_STATUS,
  REVIEW_STATUS,
  formatKurus,
} from "../labels";
import type { LessonDetail } from "../types";
import { ConnectionCell } from "../components/connection";

const PARTY: Record<string, string> = { TUTOR: "Eğitmen", STUDENT: "Öğrenci", ADMIN: "Ekip" };

const at = (value: string | null) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : "—");

/**
 * /lessons/:id (permission `lessons.view`): the lesson, its cancellation and request, the
 * student's hour movements, the tutor's earning, the review and the recordings (watching one
 * needs `lessons.recordings` and is written to the audit log).
 */
export function LessonShowPage() {
  const { id = "" } = useParams();
  const { result: lesson, query } = useOne<LessonDetail>({ resource: "lessons", id });
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });

  return (
    <Show
      title={
        lesson ? `${lesson.subject} · ${dayjs(lesson.startsAt).format(DATE_TIME_FORMAT)}` : "Ders"
      }
      isLoading={query.isLoading}
      canEdit={false}
      canDelete={false}
    >
      {lesson ? (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={13}>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Durum">
                  <Tag color={LESSON_STATUS[lesson.status]?.color}>
                    {LESSON_STATUS[lesson.status]?.label}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Zaman">
                  {at(lesson.startsAt)} – {dayjs(lesson.endsAt).format("HH:mm")} · {lesson.hours}{" "}
                  saat
                </Descriptions.Item>
                <Descriptions.Item label="Eğitmen">
                  {canSeeTutors?.can ? (
                    <Link to={`/tutors/${lesson.tutor.id}`}>{lesson.tutor.name}</Link>
                  ) : (
                    lesson.tutor.name
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Öğrenci">
                  {canSeeStudents?.can ? (
                    <Link to={`/students/${lesson.student.id}`}>{lesson.student.name}</Link>
                  ) : (
                    lesson.student.name
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Saat kaynağı">
                  {HOUR_BUCKETS[lesson.hourBucket] ?? lesson.hourBucket}
                </Descriptions.Item>
                <Descriptions.Item label="Rezervasyon">{at(lesson.createdAt)}</Descriptions.Item>
                {lesson.message ? (
                  <Descriptions.Item label="Öğrencinin notu">{lesson.message}</Descriptions.Item>
                ) : null}
                {lesson.completedAt ? (
                  <Descriptions.Item label="Tamamlandı">{at(lesson.completedAt)}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Ders kaydı">
                  <Recordings lessonId={lesson.id} recordings={lesson.recordings} />
                </Descriptions.Item>
              </Descriptions>

              {lesson.status === "CANCELLED" || lesson.cancellationRequestedBy ? (
                <Card size="small" title="İptal">
                  <Descriptions column={1} size="small">
                    {lesson.cancelledBy ? (
                      <Descriptions.Item label="İptal eden">
                        {PARTY[lesson.cancelledBy]} · {at(lesson.cancelledAt)}
                        {lesson.isLateCancellation ? " · geç iptal" : ""}
                      </Descriptions.Item>
                    ) : null}
                    {lesson.cancellationReason ? (
                      <Descriptions.Item label="Neden">
                        {CANCELLATION_REASONS[lesson.cancellationReason] ??
                          lesson.cancellationReason}
                        {lesson.cancellationNote ? ` · ${lesson.cancellationNote}` : ""}
                      </Descriptions.Item>
                    ) : null}
                    {lesson.cancellationRequestedBy ? (
                      <Descriptions.Item label="Geç iptal talebi">
                        {PARTY[lesson.cancellationRequestedBy]} ·{" "}
                        {at(lesson.cancellationRequestedAt)} ·{" "}
                        {lesson.cancellationRequestStatus
                          ? CANCELLATION_REQUEST_STATUS[lesson.cancellationRequestStatus]?.label
                          : "—"}
                        {lesson.cancellationDecidedAt
                          ? ` (${at(lesson.cancellationDecidedAt)})`
                          : ""}
                      </Descriptions.Item>
                    ) : null}
                  </Descriptions>
                </Card>
              ) : null}
            </Space>
          </Col>
          <Col xs={24} lg={11}>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
              <Card size="small" title="Öğrencinin saat hareketleri">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={lesson.hourEntries}
                  locale={{ emptyText: "Hareket yok" }}
                  columns={[
                    {
                      title: "Zaman",
                      dataIndex: "createdAt",
                      render: (value: string) => at(value),
                    },
                    {
                      title: "Hareket",
                      dataIndex: "reason",
                      render: (value: string) => HOUR_REASONS[value] ?? value,
                    },
                    {
                      title: "Kaynak",
                      dataIndex: "bucket",
                      render: (value: string) => HOUR_BUCKETS[value] ?? value,
                    },
                    {
                      title: "Saat",
                      dataIndex: "deltaHours",
                      render: (value: number) => (value > 0 ? `+${value}` : String(value)),
                    },
                  ]}
                />
              </Card>
              <Card size="small" title="Ders içi sohbet">
                {lesson.chatMessages.length > 0 ? (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {lesson.chatMessages.map((message) => (
                      <div key={message.id}>
                        <Typography.Text type="secondary">
                          {message.sentBy === "STUDENT" ? lesson.student.name : lesson.tutor.name} ·{" "}
                          {at(message.createdAt)}
                        </Typography.Text>
                        <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                          {message.body}
                        </Typography.Paragraph>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">Derste yazışma yok</Typography.Text>
                )}
              </Card>
              <Card size="small" title="Bağlantı">
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <ConnectionCell connection={lesson.connection} />
                  {lesson.connectionLogs.length > 0 ? (
                    <Table
                      size="small"
                      rowKey={(log) => `${log.party}-${log.kind}-${log.createdAt}`}
                      pagination={false}
                      dataSource={lesson.connectionLogs}
                      columns={[
                        {
                          title: "Saat",
                          dataIndex: "createdAt",
                          render: (value: string) => at(value),
                        },
                        {
                          title: "Kim",
                          dataIndex: "party",
                          render: (party: string) => (party === "STUDENT" ? "Öğrenci" : "Eğitmen"),
                        },
                        {
                          title: "Olay",
                          key: "event",
                          render: (_, log) => connectionEvent(log),
                        },
                      ]}
                    />
                  ) : (
                    <Typography.Text type="secondary">Bağlantı kaydı yok</Typography.Text>
                  )}
                </Space>
              </Card>
              <Card size="small" title="Eğitmen kazancı">
                {lesson.earning ? (
                  <Typography.Text>
                    {formatKurus(lesson.earning.amountKurus)} · {at(lesson.earning.createdAt)}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">Kazanç yok</Typography.Text>
                )}
              </Card>
              <Card size="small" title="Değerlendirme">
                {lesson.review ? (
                  <Space direction="vertical" size={4}>
                    <span>
                      {lesson.review.rating}/5{" "}
                      <Tag color={REVIEW_STATUS[lesson.review.status]?.color}>
                        {REVIEW_STATUS[lesson.review.status]?.label}
                      </Tag>
                    </span>
                    {lesson.review.comment ? (
                      <Typography.Paragraph>{lesson.review.comment}</Typography.Paragraph>
                    ) : null}
                  </Space>
                ) : (
                  <Empty description="Değerlendirme yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      ) : null}
    </Show>
  );
}

/** The lesson's recordings (6 months); each "İzle" asks the API for the video and is audited. */
function Recordings({
  lessonId,
  recordings,
}: {
  lessonId: string;
  recordings: LessonDetail["recordings"];
}) {
  const { data: canWatch } = useCan({ resource: "lessons", action: "recordings" });
  const { open } = useNotification();
  const [watching, setWatching] = useState<{ id: string; url: string } | null>(null);

  if (recordings.length === 0) {
    return <Typography.Text type="secondary">Kayıt yok</Typography.Text>;
  }

  async function watch(recordingId: string) {
    try {
      const { url } = await api<{ url: string }>(
        `/api/office/lessons/${encodeURIComponent(lessonId)}/recordings/${encodeURIComponent(recordingId)}/watch`,
        { method: "POST" },
      );
      setWatching({ id: recordingId, url });
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    }
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {recordings.map((recording, index) => (
        <Space key={recording.id} wrap>
          <Typography.Text>
            Kayıt {index + 1} · {dayjs(recording.startedAt).format("HH:mm")} ·{" "}
            {Math.max(1, Math.round(recording.durationSeconds / 60))} dk
          </Typography.Text>
          {canWatch?.can ? (
            <Button size="small" onClick={() => void watch(recording.id)}>
              İzle
            </Button>
          ) : null}
        </Space>
      ))}
      {!canWatch?.can ? (
        <Typography.Text type="secondary">İzlemek için ders kaydı yetkisi gerekir.</Typography.Text>
      ) : null}
      {watching ? (
        <video
          key={watching.id}
          controls
          autoPlay
          src={watching.url}
          style={{ width: "100%", maxWidth: 720, background: "#000" }}
        />
      ) : null}
    </Space>
  );
}

const QUALITY_LABELS: Record<string, string> = {
  excellent: "Bağlantı çok iyi",
  good: "Bağlantı iyi",
  poor: "Bağlantı zayıfladı",
  lost: "Bağlantı koptu",
};

function connectionEvent(log: LessonDetail["connectionLogs"][number]): string {
  if (log.kind === "SPEED_TEST") {
    return `Hız testi: ↓${log.downloadMbps ?? 0} ↑${log.uploadMbps ?? 0} Mbps, ${log.latencyMs ?? 0} ms`;
  }
  if (log.kind === "QUALITY") return QUALITY_LABELS[log.quality ?? ""] ?? String(log.quality);
  return log.kind === "RECONNECTING" ? "Yeniden bağlanıyor" : "Yeniden bağlandı";
}
